// The fields a capture step asks for.
//
// A workflow holds an ordered list of STEPS, and each step is a form the tenant
// designs rather than one this repo ships: its `params` DECLARE the fields, and
// the app compiles that declaration into a form at render time through
// @repoflow/components/types-values — the same runtime pagews' sections use,
// over the same `/Types/Param` shape, keyed by the same param-type names.
//
// What is NOT shared is the registry. ./params/utils declares wfws' own set of
// param types and says why it is narrower than pagews'.
//
// A step is the `/Types/Type` of that runtime with a `label` in front of it: the
// runtime resolves a values form against a type called "root", so a step is
// compiled as `[{ name: "root", params: step.params }, ...subforms]` — the
// step's own params first, then every SUBFORM the workflow declares. A subform
// is a `/Workflow/Subform`: a named params list with no label and no place in
// the step order, which exists only to be named by an `object` param (or a
// `list` of them) on any step, or on another subform. They are held once, on
// the workflow, so that the same address block can be asked for on three
// steps without being declared three times. See `stepTypes` below, which is
// the one place that adaptation is written.
import * as Params from "./params";
import UtilsSchema from "@repoflow/schema/utils";
import * as SchemaLib from "@repoflow/schema";

// /Name/Required, spelled out, with "root" excluded — for the reason at the
// subform's `name` below. Spelled out rather than `$ref` plus a `pattern`,
// because keywords beside a $ref are dropped when the reference resolves and
// the exclusion would silently not apply (the api test that pins it is what
// found that).
const SUBFORM_NAME = {
  type: "string",
  maxLength: 256,
  minLength: 1,
  pattern: "^(?!root$)[a-z0-9]+(?:-[a-z0-9.]+)*$",
};

const schema = [
  ...UtilsSchema,
  ...Params.schema,
  {
    id: "/Workflow/Step",
    type: "object",
    required: ["name"],
    properties: {
      // What the step is addressed by — in a capture's payload, and in the URL
      // a filled-in step will one day be posted to. Lowercase-kebab, so it is
      // safe in both. Filled in from the label by the designer, the way a
      // param's name is.
      name: {
        label: "Name",
        $ref: "/Name/Required",
        minLength: 1,
        default: "",
      },
      // What the person filling the step in is shown as its heading.
      label: {
        label: "Label",
        type: "string",
        default: "",
      },
      description: {
        label: "Description",
        type: "string",
        textarea: true,
        default: "",
      },
      params: {
        label: "Fields",
        type: "array",
        items: { $ref: "/Types/Param" },
        // Turns on drag-reorder in the list widget, which keys rows by this
        // property rather than by array position. The order here is the order
        // the fields are asked in.
        index: "name",
        default: [],
      },
    },
  },
  {
    // A subform: a named, reusable group of fields. What pagews calls a
    // non-root `/Types/Type`, under the name a person designing a workflow
    // would use. No label — a subform is never a heading of its own, it is
    // rendered inline wherever an `object` param names it.
    id: "/Workflow/Subform",
    type: "object",
    required: ["name"],
    properties: {
      // Everything /Name/Required allows except the one word the runtime
      // reserves: every step is compiled as the type called "root", so a
      // subform of that name would be shadowed on every step and reachable
      // from none.
      name: { label: "Name", ...SUBFORM_NAME, default: "" },
      description: {
        label: "Description",
        type: "string",
        textarea: true,
        default: "",
      },
      params: {
        label: "Fields",
        type: "array",
        items: { $ref: "/Types/Param" },
        index: "name",
        default: [],
      },
    },
  },
  {
    // What the designer edits: the steps, as one wide list. The same shape
    // `/Input/Types` has for a section, under the name a workflow uses.
    id: "/Input/Steps",
    type: "object",
    properties: {
      steps: {
        label: "Steps",
        type: "array",
        items: { $ref: "/Workflow/Step" },
        index: "name",
        default: [],
      },
    },
  },
  {
    // What the Subforms tab edits: the workflow's subforms, as one wide list.
    id: "/Input/Subforms",
    type: "object",
    properties: {
      subforms: {
        label: "Subforms",
        type: "array",
        items: { $ref: "/Workflow/Subform" },
        index: "name",
        default: [],
      },
    },
  },
];

/** A step with every declared default filled in, under this name. */
export const defaultStepValues = (name = "") => {
  const vals = SchemaLib.defaultValues(schema, { $ref: "/Workflow/Step" });
  vals.name = name;
  return vals;
};

/** A subform with every declared default filled in, under this name. */
export const defaultSubformValues = (name = "") => {
  const vals = SchemaLib.defaultValues(schema, { $ref: "/Workflow/Subform" });
  vals.name = name;
  return vals;
};

/**
 * One step as the `types` array types-values compiles.
 *
 * The runtime hard-codes "root" as the type it resolves the values form
 * against, so the step's own params go in under that name, and every subform
 * the workflow declares follows — an `object` param on the step resolves to
 * `/Type/<subform>` and generateSchema has to have emitted it. The subforms
 * are the WORKFLOW's, not the step's: the same list is handed in for every
 * step, which is what lets two steps share one.
 *
 * Only `name` and `params` are carried: `description` is for the designer,
 * and the runtime would ignore it anyway.
 */
export const stepTypes = (step, subforms = []) => [
  { name: "root", params: (step && step.params) || [] },
  ...(subforms || []).map(({ name, params }) => ({ name, params: params || [] })),
];

/**
 * The subform names that `params` reach, through `object` params directly
 * and through the item declaration of a `list`, however deep.
 */
const namedSubforms = (params, found = new Set()) => {
  for (const param of params || []) {
    let decl = param;
    // A list's config is the item's declaration, which may itself be a list.
    while (decl && decl.type === "list") {
      decl = decl.config;
    }
    if (decl && decl.type === "object" && decl.config && decl.config.type) {
      found.add(decl.config.type);
    }
  }
  return found;
};

/**
 * The subforms `step` names that `subforms` does not declare, in the order
 * they are first reached — including through a subform that is itself
 * reached. Empty means the step compiles.
 *
 * generateSchema does not check this: it emits `$ref: /Type/<name>` and the
 * form throws on the dangling reference from inside its render, which is a
 * blank screen where the designer should be. The Preview tab asks this first
 * and says which name is missing instead.
 */
export const missingSubforms = (step, subforms = []) => {
  const declared = new Map((subforms || []).map((sf) => [sf.name, sf]));
  const missing = [];
  const seen = new Set();
  const visit = (params) => {
    for (const name of namedSubforms(params)) {
      if (seen.has(name)) continue;
      seen.add(name);
      if (declared.has(name)) {
        visit(declared.get(name).params);
      } else {
        missing.push(name);
      }
    }
  };
  visit(step && step.params);
  return missing;
};

/**
 * Whether a param takes its value from the share link's query string.
 *
 * A hidden field is declared but never rendered, so the visitor cannot answer
 * it — which makes it the place for what the PAGE knows rather than the
 * person: which site the form was embedded on, which campaign sent them. The
 * embedding page says so in the frame's URL (`?embed=1&source=…`), and the
 * fill app copies `source` into the field of that name. Text only, because a
 * query string carries text and a text param stores it unchanged.
 */
export const prefillable = (param) =>
  Boolean(param && param.hidden && param.type === "text");

/** The names of the fields across `steps` that a link can fill in, each once. */
export const hiddenFields = (steps = []) => [
  ...new Set(
    (steps || []).flatMap((step) =>
      ((step && step.params) || []).filter(prefillable).map((param) => param.name)
    )
  ),
];

/**
 * Whether a value counts as an answer to a required field.
 *
 * Exported so the fill app can ask it of ONE field — the note it puts under a
 * required field the visitor left empty — with the same predicate the step's
 * Next button asks of all of them through `unansweredRequired`, so the two
 * cannot disagree about what "answered" means.
 */
export const answered = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * The required fields `values` leaves unanswered, as dotted paths from the
 * step's root — through `object` params into their subforms, and into each
 * row of a `list` of them.
 *
 * WHY THIS EXISTS BESIDE THE SCHEMA. types-values compiles `required: true`
 * into JSON Schema `required`, which means "the key is present" — and every
 * text param is seeded with its default, `""`, so an empty answer to a
 * required text field satisfies the compiled schema. That is the right
 * meaning for a section's values in pagews, where a slot may legitimately be
 * blank; it is the wrong one for a capture, where "required" was the tenant
 * saying the visitor must answer. So the fill app asks this after
 * `SchemaLib.issues`, and the API asks it before it stores — the same
 * function, so the two cannot disagree.
 *
 * A `checkbox` is never unanswered: unticked is an answer. A number is
 * unanswered only when nothing was entered.
 */
export const unansweredRequired = (step, subforms = [], values = {}) => {
  const declared = new Map((subforms || []).map((sf) => [sf.name, sf]));
  const missing = [];

  const visit = (params, vals, prefix, seen) => {
    for (const param of params || []) {
      const value = vals ? vals[param.name] : undefined;
      const path = prefix ? `${prefix}.${param.name}` : param.name;

      if (param.required && param.type !== "checkbox" && !answered(value)) {
        missing.push(path);
        continue;
      }

      // Into a subform, whether asked for directly or as the rows of a list.
      let decl = param;
      while (decl && decl.type === "list") decl = decl.config;
      const subform = decl && decl.type === "object" && decl.config && declared.get(decl.config.type);
      if (!subform || seen.has(subform.name)) continue;

      const rows = param.type === "list" ? (Array.isArray(value) ? value : []) : [value];
      rows.forEach((row, index) => {
        if (row && typeof row === "object") {
          const at = param.type === "list" ? `${path}[${index}]` : path;
          visit(subform.params, row, at, new Set([...seen, subform.name]));
        }
      });
    }
  };

  visit(step && step.params, values, "", new Set());
  return missing;
};

export default schema;
